// The six roles, as ids. Mirror of the backend's utils/roles.js.
//
// Roles used to be strings on the user object — 'storehead', 'storeuser',
// 'servicetstaff'. They are now rows in role_master and the API serves the id
// (backend migration 018). Screens must never compare a role to a literal:
// import from here instead.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY THIS ACCEPTS BOTH FORMS
//
// A phone keeps its session in Capacitor Preferences and does not re-read it
// until the next login. So on the day the backend migrates, a user already
// signed in is holding `role: "storehead"` in storage while the API has started
// answering with `role: 4`. Both have to work, at the same time, in the same
// build — otherwise every logged-in user loses their sidebar until they sign
// out and back in, and they have no reason to think of doing that.
//
// This file is duplicated rather than shared because the two apps deploy
// separately and a shared package would have to be versioned across both. The
// pair is small, and roles.test.js on the backend pins the mapping; keep the two
// in step by hand.
// ─────────────────────────────────────────────────────────────────────────────

// role_master.id.
export const ROLE = Object.freeze({
  SERVICE_HEAD: 1,
  SERVICE_DEPT_HEAD: 2,
  SERVICE_STAFF: 3,
  STORE_HEAD: 4,
  STORE_DEPT_HEAD: 5,
  STORE_STAFF: 6,
});

// role_master.role_title — the machine name.
export const ROLE_TITLES = Object.freeze({
  [ROLE.SERVICE_HEAD]: "service_head",
  [ROLE.SERVICE_DEPT_HEAD]: "service_dept_head",
  [ROLE.SERVICE_STAFF]: "service_staff",
  [ROLE.STORE_HEAD]: "store_head",
  [ROLE.STORE_DEPT_HEAD]: "store_dept_head",
  [ROLE.STORE_STAFF]: "store_staff",
});

// role_master.role_display — what a person is shown.
export const ROLE_LABELS = Object.freeze({
  [ROLE.SERVICE_HEAD]: "Service Head",
  [ROLE.SERVICE_DEPT_HEAD]: "Service HOD",
  [ROLE.SERVICE_STAFF]: "Service Staff",
  [ROLE.STORE_HEAD]: "Store Head",
  [ROLE.STORE_DEPT_HEAD]: "Store HOD",
  [ROLE.STORE_STAFF]: "Store Staff",
});

// The pre-018 spellings, one for one. 'storeuser' and 'servicetstaff' are the
// names the old ENUM actually carried, typo included.
const LEGACY_ROLE_IDS = Object.freeze({
  servicehead: ROLE.SERVICE_HEAD,
  servicedepthead: ROLE.SERVICE_DEPT_HEAD,
  servicetstaff: ROLE.SERVICE_STAFF,
  storehead: ROLE.STORE_HEAD,
  storedepthead: ROLE.STORE_DEPT_HEAD,
  storeuser: ROLE.STORE_STAFF,
});

const ROLE_IDS_BY_NAME = Object.freeze({
  ...LEGACY_ROLE_IDS,
  ...Object.fromEntries(
    Object.entries(ROLE_TITLES).map(([id, title]) => [title, Number(id)]),
  ),
});

export const ALL_ROLE_IDS = Object.freeze(Object.values(ROLE));

// Kumudu's own people versus a customer store's staff.
export const SERVICE_ROLES = Object.freeze([
  ROLE.SERVICE_HEAD,
  ROLE.SERVICE_DEPT_HEAD,
  ROLE.SERVICE_STAFF,
]);

export const STORE_ROLES = Object.freeze([
  ROLE.STORE_HEAD,
  ROLE.STORE_DEPT_HEAD,
  ROLE.STORE_STAFF,
]);

// The two roles that run their whole side of the house — the ones allowed to
// switch store, and from Phase 1 to switch branch.
export const HEAD_ROLES = Object.freeze([ROLE.SERVICE_HEAD, ROLE.STORE_HEAD]);

export const DEPT_HEAD_ROLES = Object.freeze([
  ROLE.SERVICE_DEPT_HEAD,
  ROLE.STORE_DEPT_HEAD,
]);

/**
 * Whatever names a role → its id, or null.
 *
 * Takes an id (4), a numeric string ("4"), a role_title ("store_head") or a
 * pre-018 value ("storehead"). Never guesses: anything else is null.
 */
export const toRoleId = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number") {
    return ALL_ROLE_IDS.includes(value) ? value : null;
  }

  const text = String(value).trim();
  if (!text) return null;

  if (/^\d+$/.test(text)) {
    const id = parseInt(text, 10);
    return ALL_ROLE_IDS.includes(id) ? id : null;
  }

  return ROLE_IDS_BY_NAME[text.toLowerCase()] ?? null;
};

export const roleTitle = (value) => ROLE_TITLES[toRoleId(value)] ?? null;

/**
 * What to show for a role. Falls back to printing the raw value, so an
 * unfamiliar role appears verbatim rather than as a blank.
 */
export const roleLabel = (value) => {
  const id = toRoleId(value);
  if (id !== null) return ROLE_LABELS[id];
  return value === null || value === undefined ? null : String(value);
};

export const isRole = (value, ...ids) => {
  const id = toRoleId(value);
  return id !== null && ids.flat().includes(id);
};

export const isServiceRole = (value) => isRole(value, SERVICE_ROLES);
export const isStoreRole = (value) => isRole(value, STORE_ROLES);
export const isHead = (value) => isRole(value, HEAD_ROLES);
export const isDeptHead = (value) => isRole(value, DEPT_HEAD_ROLES);
export const isStaffRole = (value) =>
  isRole(value, [ROLE.SERVICE_STAFF, ROLE.STORE_STAFF]);
export const isStoreHead = (value) => isRole(value, ROLE.STORE_HEAD);
export const isServiceHead = (value) => isRole(value, ROLE.SERVICE_HEAD);
export const isServiceDeptHead = (value) =>
  isRole(value, ROLE.SERVICE_DEPT_HEAD);

// ─────────────────────────────────────────────────────────────────────────────
// Branches
//
// A branch ATTRIBUTES a transaction, it does not isolate one. Transaction lists
// stay store-wide — a booking taken at branch A must be payable at branch B —
// so nothing here is a read filter. These helpers only decide who may move the
// session between branches, and who may look at all of them at once.
//
// Mirror of the backend's utils/roles.js; keep the two in step by hand, the
// same way the rest of this file is kept in step.
//
// STORE_DEPT_HEAD is pinned on the strength of "only the store head switches
// branches". It is the one role that was never stated outright, and it is a
// one-line move on each side if that turns out to be wrong.
// ─────────────────────────────────────────────────────────────────────────────

// Must carry a branch_id, and are confined to it.
export const BRANCH_PINNED_ROLES = Object.freeze([
  ROLE.STORE_DEPT_HEAD,
  ROLE.STORE_STAFF,
]);

// branch_id is NULL and means "every branch". Kumudu's own people support every
// store, so confining them to one branch would hide work from them.
export const BRANCH_UNSCOPED_ROLES = Object.freeze(SERVICE_ROLES);

// May move the session between the branches of the store they are in.
export const BRANCH_SWITCH_ROLES = Object.freeze(HEAD_ROLES);

export const isBranchPinned = (value) => isRole(value, BRANCH_PINNED_ROLES);
export const isBranchUnscoped = (value) => isRole(value, BRANCH_UNSCOPED_ROLES);

/**
 * May this ROLE switch branch — the role half of the test, and only that half.
 *
 * Whether a switcher is actually drawn also needs the store to have more than
 * one branch. That is already folded into the canSwitchBranch value on
 * AutoTaskContext (which the server answers, so the two sides cannot disagree)
 * and into useSwitchableBranches().canSwitch. Prefer those inside components.
 * This one is for deciding things about a role in the abstract — which fields a
 * user form should show, for instance, where there is no session to ask.
 *
 * Named with the "Role" suffix precisely so it cannot be mistaken at a glance
 * for the context value of the almost-identical name.
 */
export const canSwitchBranchRole = (value) => isRole(value, BRANCH_SWITCH_ROLES);

/**
 * May this role look at the branch report — every shop's figures side by side.
 *
 * Owner's eyes only. Comparing the branches is a management question ("which of
 * my shops is carrying the book"), and the answer names what every other shop
 * took in — so a person who runs one branch has no business reading it about
 * the others, and a counter hand has no business reading it at all. That is
 * HEAD_ROLES: the store head owns the store, and a Kumudu service head supports
 * it and already sees every screen.
 *
 * The rule lives here rather than in the screens because there are now two
 * doors onto the report — the Daily Tick rail and the pawn Reports strip — and
 * two copies of a permission test is how one of them ends up laxer than the
 * other. The backend downgrades an over-wide request to the caller's own branch
 * rather than refusing it, so this is what actually keeps the screen shut.
 */
export const canViewBranchReport = (value) => isHead(value);

/**
 * May this role change a pawn record already on file — an existing payment,
 * receipt or settlement — or the pawn settings.
 *
 * Every role may RAISE a new payment, receipt or settlement. Changing one after
 * the fact is a different thing: an edit rewrites the store's books (the ledger
 * is rebuilt from the edited values), so a counter hand who could edit could
 * quietly alter a loan nobody would think to re-check. That is the Store Head
 * and the Store HOD only. Kumudu's service roles support the store and already
 * reach every screen, so they keep it too.
 *
 * Deny by default: an unknown or not-yet-hydrated role is view-only. The
 * backend runs the same test (utils/roles.js canEditPawnRecords) on every
 * update endpoint, so this only decides what is drawn — it is not what keeps a
 * record safe. Keep the two lists in step by hand.
 */
export const PAWN_EDIT_ROLES = Object.freeze([
  ...SERVICE_ROLES,
  ROLE.STORE_HEAD,
  ROLE.STORE_DEPT_HEAD,
]);

export const canEditPawnRecords = (value) => isRole(value, PAWN_EDIT_ROLES);

/**
 * The `{ [id]: label }` map a role dropdown renders from.
 *
 * `assignableRolesFor` narrows it to what this person may hand out. An empty map
 * is what the user screens already treat as "you cannot add users", so no caller
 * needs a separate permission check.
 *
 * NOTE — Service HOD is deliberately absent, because it was absent before. The
 * `availableRoles` effect this replaces admitted 'servicehead' and
 * 'servicetstaff' and nobody else, so a Service HOD can assign no roles while a
 * Service Staff beneath them can assign all six. That looks like an oversight
 * rather than a decision, but changing who may create users is a permissions
 * change and does not belong in a migration that only swaps strings for ids.
 * Left exactly as found, on purpose.
 */
export const roleOptions = (ids = ALL_ROLE_IDS) =>
  Object.fromEntries(ids.map((id) => [id, ROLE_LABELS[id]]));

export const assignableRolesFor = (value) => {
  if (isRole(value, [ROLE.SERVICE_HEAD, ROLE.SERVICE_STAFF]))
    return roleOptions(ALL_ROLE_IDS);
  if (isStoreHead(value))
    return roleOptions([ROLE.STORE_DEPT_HEAD, ROLE.STORE_STAFF]);
  return {};
};
